import type { FunctionComponent } from "react";

import Card from "~/components/Card/Card";
import HorizontalStats from "~/components/HorizontalStats/HorizontalStats";
import type { HorizontalStat } from "~/components/HorizontalStats/horizontalStatsTypes.ts";
import OverflowPercentageChart from "~/components/OverflowPercentageChart/OverflowPercentageChart";
import type { ProjectRevenueReport } from "~/modules/project/api/projectRevenueReport/projectRevenueReportTypes.ts";
import { getInvoicedTotalCents } from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

// Inverted ring colors: red below 100% invoiced, green at or above 100%.
const BELOW_TARGET_COLOR = "#f63b3b";
const AT_TARGET_COLOR = "#36af2d";

type Props = {
	report: ProjectRevenueReport;
};

const ProjectRevenueStatsCard: FunctionComponent<Props> = ({ report }) => {
	const billableTrackedCents = report.totalTrackedBillableCents;
	const invoicedTotalCents = getInvoicedTotalCents(report);
	const notInvoicedBillableCents = billableTrackedCents - invoicedTotalCents;
	const invoicedPercentage = billableTrackedCents > 0
		? (invoicedTotalCents / billableTrackedCents) * 100
		: 0;

	const stats: HorizontalStat[] = [
		{
			label: "Volumen aus Erfassung",
			value: formatCentsToCurrency(billableTrackedCents),
		},
		{
			label: "Nicht abrechenbar",
			value: <span className="text-gray-400">{formatCentsToCurrency(report.totalTrackedNotBillableCents)}</span>,
		},
		{
			label: "Abgerechnet",
			value: formatCentsToCurrency(invoicedTotalCents),
		},
		{
			label: "Nicht abgerechnet (offen)",
			value: formatCentsToCurrency(notInvoicedBillableCents),
			isHighlighted: notInvoicedBillableCents < 0,
		},
	];

	return <Card>
		<HorizontalStats stats={stats}>
			<OverflowPercentageChart percentage={invoicedPercentage}
									 size={80}
									 thickness={0.4}
									 arcColor={BELOW_TARGET_COLOR}
									 overflowColor={AT_TARGET_COLOR}>
				<div className="text-sm font-bold">{invoicedPercentage.toFixed(0)}%</div>
			</OverflowPercentageChart>
		</HorizontalStats>
	</Card>;
};

export default ProjectRevenueStatsCard;
